import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Footer from '../../../components/feature/Footer';
import TableOfContents, { generateTocItems } from '../../../components/feature/TableOfContents';

const tocItems = generateTocItems([
  'The Aftermath: What Happens After a House Fire in El Paso',
  'Can You Sell a Fire Damaged House in Texas',
  'Who Buys Fire Damaged Property and Why',
  'Your Three Options for Selling a Fire Damaged Home',
  'How Insurance Affects Your Fire Damaged Property Sale',
  'What Fire Damage Repairs Actually Cost in El Paso',
  'The Cash Sale Process for Fire Damaged Homes',
  'Fire Damage Disclosure Requirements in Texas',
  'How We Evaluate Fire Damaged Properties in El Paso',
  'Frequently Asked Questions About Selling Fire Damaged Property',
  'Take the First Step Toward Recovery',
]);

export default function FireDamagedPropertyElPaso() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
              <i className="ri-home-4-fill text-[#D4AF37] text-xl"></i>
            </div>
            <span className="text-xl font-bold text-black">Cash For Houses El Paso</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/#how-it-works" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">
              How It Works
            </Link>
            <Link to="/#about-elpaso" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">
              About El Paso
            </Link>
            <Link to="/#benefits" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">
              Benefits
            </Link>
            <Link to="/get-cash" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">
              Blog
            </Link>
            <Link to="/#faq" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">
              FAQ
            </Link>
            <a href="tel:9157457883" className="px-6 py-2.5 bg-black text-white text-sm font-semibold rounded-full hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer">
              Call: 915-745-7883
            </a>
          </div>
          <div className="md:hidden">
            <a href="tel:9157457883" className="px-4 py-2 bg-black text-white text-sm font-semibold rounded-full whitespace-nowrap cursor-pointer">
              Call Now
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/get-cash" className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-black transition-colors mb-8 cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Blog
          </Link>

          <div className="mb-8">
            <span className="inline-block px-4 py-1.5 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-4">
              FIRE DAMAGE
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-black mb-6 leading-tight">
              Companies That Buy Fire Damaged Property in El Paso: Your Complete Guide to Selling After a House Fire
            </h1>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <i className="ri-user-line"></i>
                <span>Cash For Houses El Paso</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                <span>24 min read</span>
              </div>
            </div>
          </div>

          <div className="rounded-3xl overflow-hidden shadow-xl mb-12 h-96">
            <img
              src="https://public.readdy.ai/ai/img_res/521fdd7e0850db47972edaf358a8dec2.jpg"
              alt="Fire damaged property for sale in El Paso Texas"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            The smell of smoke still lingers. The insurance adjuster has come and gone. You&apos;re standing in what used to be your living
            room, staring at charred walls and water-soaked floors, and one thought keeps circling your mind: what do I do now? If you own a
            fire damaged property in El Paso, you&apos;re facing one of the most overwhelming situations a homeowner can experience. The good
            news is that companies exist specifically to buy fire damaged homes&mdash;and you have more options than you think. This guide
            walks you through every step of selling a fire damaged property, from understanding your insurance situation to closing with cash
            in hand.
          </p>

          <TableOfContents items={tocItems} />

          {/* Section 1 */}
          <h2 id="the-aftermath-what-happens-after-a-house-fire-in-el-paso" className="text-4xl font-bold text-black mt-16 mb-6">
            The Aftermath: What Happens After a House Fire in El Paso
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            House fires are devastating. According to the National Fire Protection Association, a home structure fire is reported every 93 seconds in the United States. In El Paso, where dry desert conditions and aging electrical systems create additional risk factors, fire damage can range from minor smoke damage in one room to complete structural loss.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            After the fire trucks leave, homeowners face a cascade of urgent decisions:
          </p>

          <ul className="list-disc pl-8 mb-6 text-gray-700 space-y-2">
            <li><strong>Where will you live?</strong> If the home is uninhabitable, you need temporary housing immediately</li>
            <li><strong>What about your mortgage?</strong> Monthly payments continue even if you can&apos;t live there</li>
            <li><strong>Will insurance cover everything?</strong> Many policies have coverage gaps or depreciation clauses</li>
            <li><strong>Should you rebuild or sell?</strong> Restoration can take 6-12 months and cost more than expected</li>
            <li><strong>What if you&apos;re underwater on the loan?</strong> Fire damage can push equity into negative territory</li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-6">
            The financial pressure is immediate and intense. You&apos;re paying for temporary housing, continuing mortgage payments, and possibly facing repair estimates that exceed your insurance payout. Meanwhile, the property sits vacant, deteriorating further from weather exposure and potential vandalism.
          </p>

          <div className="bg-amber-50 border-l-4 border-[#D4AF37] p-6 my-8 rounded-r-lg">
            <p className="text-gray-800 font-semibold mb-2">
              <i className="ri-lightbulb-flash-line text-[#D4AF37] mr-2"></i>
              Reality Check
            </p>
            <p className="text-gray-700 text-base">
              Most homeowners don&apos;t realize they can sell a fire damaged property as-is. You don&apos;t have to rebuild. You don&apos;t have to clean up. Companies that specialize in buying fire damaged homes will purchase your property in its current condition and handle everything else.
            </p>
          </div>

          {/* Section 2 */}
          <h2 id="can-you-sell-a-fire-damaged-house-in-texas" className="text-4xl font-bold text-black mt-16 mb-6">
            Can You Sell a Fire Damaged House in Texas?
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Yes, absolutely. Texas law does not prohibit selling fire damaged property. However, you must follow specific disclosure requirements (which we&apos;ll cover in detail later). The key legal considerations are:
          </p>

          <div className="bg-white border border-gray-200 rounded-xl p-6 my-8">
            <h3 className="text-xl font-bold text-black mb-4">Legal Requirements for Selling Fire Damaged Property in Texas</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-black mb-2">1. Seller&apos;s Disclosure Notice</h4>
                <p className="text-gray-700 text-base">
                  You must complete the Texas Real Estate Commission&apos;s Seller&apos;s Disclosure Notice and specifically note the fire damage, including the date of the fire, extent of damage, and any repairs made.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-2">2. Material Defects</h4>
                <p className="text-gray-700 text-base">
                  Fire damage is considered a material defect. You cannot hide it or fail to mention it. Doing so opens you to legal liability after the sale.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-2">3. Insurance Claim Status</h4>
                <p className="text-gray-700 text-base">
                  If you received an insurance payout for repairs but are selling instead, you may need to disclose this to the buyer. Some insurance companies require repayment if repairs aren&apos;t completed.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-black mb-2">4. Mortgage Lender Approval</h4>
                <p className="text-gray-700 text-base">
                  If you have a mortgage, your lender may have a claim on insurance proceeds. You&apos;ll need to coordinate the sale with your lender, especially if the property value has dropped below the loan balance.
                </p>
              </div>
            </div>
          </div>

          <p className="text-gray-700 leading-relaxed mb-6">
            The good news: cash buyers who specialize in fire damaged properties are familiar with all these requirements and can guide you through the process. They&apos;ve handled dozens or hundreds of similar transactions and know exactly what documentation is needed.
          </p>

          {/* Section 3 */}
          <h2 id="who-buys-fire-damaged-property-and-why" className="text-4xl font-bold text-black mt-16 mb-6">
            Who Buys Fire Damaged Property and Why?
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Several types of buyers actively seek fire damaged properties in El Paso. Understanding who they are and what they&apos;re looking for helps you evaluate offers and choose the right buyer for your situation.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-gear-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Cash Home Buyers</h3>
              <p className="text-gray-700 text-base mb-3">
                Companies like ours specialize in buying distressed properties. We purchase fire damaged homes as-is, handle all repairs, and resell or rent them.
              </p>
              <p className="text-sm text-gray-600">
                <strong>Why they buy:</strong> They have the capital, contractor relationships, and expertise to restore properties profitably.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-hammer-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Fix-and-Flip Investors</h3>
              <p className="text-gray-700 text-base mb-3">
                Real estate investors who buy damaged properties, renovate them, and sell for a profit. They typically look for properties with good bones in desirable neighborhoods.
              </p>
              <p className="text-sm text-gray-600">
                <strong>Why they buy:</strong> Fire damaged homes often sell at significant discounts, creating profit potential after renovation.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Land Developers</h3>
              <p className="text-gray-700 text-base mb-3">
                If the structure is a total loss, developers may buy the property for the land value alone, planning to demolish and rebuild or develop multiple units.
              </p>
              <p className="text-sm text-gray-600">
                <strong>Why they buy:</strong> They&apos;re buying location and lot size, not the damaged structure. The fire damage is irrelevant to their plans.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-recycle-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Salvage Companies</h3>
              <p className="text-gray-700 text-base mb-3">
                Some companies specialize in buying severely damaged properties to salvage materials, fixtures, and land. Less common but an option for total losses.
              </p>
              <p className="text-sm text-gray-600">
                <strong>Why they buy:</strong> They can profit from salvageable materials and land value even when the structure is beyond repair.
              </p>
            </div>
          </div>

          <p className="text-gray-700 leading-relaxed mb-6">
            In El Paso&apos;s market, cash home buyers are typically your best option. They offer the fastest closings, handle all the complexity, and don&apos;t require you to make any repairs or cleanup. Fix-and-flip investors may offer slightly higher prices but often have more contingencies and longer timelines.
          </p>

          {/* Section 4 */}
          <h2 id="your-three-options-for-selling-a-fire-damaged-home" className="text-4xl font-bold text-black mt-16 mb-6">
            Your Three Options for Selling a Fire Damaged Home
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            When you own a fire damaged property in El Paso, you have three primary paths forward. Each has distinct advantages, disadvantages, and financial implications.
          </p>

          <div className="space-y-8 my-8">
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-black mb-2">Rebuild and Sell Traditionally</h3>
                  <p className="text-gray-600 text-base">Restore the property to pre-fire condition, then list with a real estate agent</p>
                </div>
              </div>
              <div className="ml-14">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <h4 className="font-semibold text-black mb-2 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-green-600"></i>
                      Advantages
                    </h4>
                    <ul className="text-gray-700 text-base space-y-1">
                      <li>• Potentially highest sale price</li>
                      <li>• Larger buyer pool</li>
                      <li>• Traditional financing available</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-black mb-2 flex items-center gap-2">
                      <i className="ri-close-circle-line text-red-600"></i>
                      Disadvantages
                    </h4>
                    <ul className="text-gray-700 text-base space-y-1">
                      <li>• 6-12 months restoration time</li>
                      <li>• Out-of-pocket costs if insurance falls short</li>
                      <li>• Continued mortgage and tax payments</li>
                      <li>• Agent commissions (5-6%)</li>
                      <li>• No guarantee of selling quickly after repairs</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Best for:</strong> Homeowners with full insurance coverage, time to wait, and emotional attachment to the property. Not ideal if you need to move on quickly or can&apos;t afford to carry the property during restoration.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-black mb-2">List As-Is with a Real Estate Agent</h3>
                  <p className="text-gray-600 text-base">Put the fire damaged property on the MLS in its current condition</p>
                </div>
              </div>
              <div className="ml-14">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <h4 className="font-semibold text-black mb-2 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-green-600"></i>
                      Advantages
                    </h4>
                    <ul className="text-gray-700 text-base space-y-1">
                      <li>• No repair costs</li>
                      <li>• Professional marketing</li>
                      <li>• Potential for competitive offers</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-black mb-2 flex items-center gap-2">
                      <i className="ri-close-circle-line text-red-600"></i>
                      Disadvantages
                    </h4>
                    <ul className="text-gray-700 text-base space-y-1">
                      <li>• Very limited buyer pool</li>
                      <li>• Buyers will be investors offering low prices</li>
                      <li>• 3-6 months average time on market</li>
                      <li>• Agent commission still applies</li>
                      <li>• Buyers often back out after inspection</li>
                      <li>• Continued carrying costs</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Best for:</strong> Properties in highly desirable locations where investors are actively looking. Not ideal if you need certainty or a fast closing.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-[#D4AF37] rounded-xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-black mb-2">Sell to a Cash Buyer</h3>
                  <p className="text-gray-600 text-base">Sell directly to a company that specializes in buying fire damaged properties</p>
                </div>
              </div>
              <div className="ml-14">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <h4 className="font-semibold text-black mb-2 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-green-600"></i>
                      Advantages
                    </h4>
                    <ul className="text-gray-700 text-base space-y-1">
                      <li>• Close in 7-14 days</li>
                      <li>• No repairs or cleanup required</li>
                      <li>• No agent commissions</li>
                      <li>• No buyer financing contingencies</li>
                      <li>• Certainty of closing</li>
                      <li>• We handle all paperwork</li>
                      <li>• Stop mortgage and tax payments immediately</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-black mb-2 flex items-center gap-2">
                      <i className="ri-close-circle-line text-red-600"></i>
                      Disadvantages
                    </h4>
                    <ul className="text-gray-700 text-base space-y-1">
                      <li>• Lower price than fully restored value</li>
                      <li>• No competitive bidding process</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg border border-[#D4AF37]">
                  <p className="text-sm text-gray-700">
                    <strong>Best for:</strong> Homeowners who need to move on quickly, can&apos;t afford restoration costs, or don&apos;t want the stress of a lengthy sale process. This is the most popular option for fire damaged properties in El Paso.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5 */}
          <h2 id="how-insurance-affects-your-fire-damaged-property-sale" className="text-4xl font-bold text-black mt-16 mb-6">
            How Insurance Affects Your Fire Damaged Property Sale
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Your insurance situation significantly impacts your options and timeline. Here&apos;s how different insurance scenarios affect selling a fire damaged property:
          </p>

          <div className="space-y-6 my-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-black mb-4 flex items-center gap-3">
                <i className="ri-shield-check-line text-green-600 text-2xl"></i>
                Scenario 1: Full Insurance Coverage
              </h3>
              <p className="text-gray-700 mb-4">
                Your insurance payout covers the full cost of restoration. You have two options:
              </p>
              <ul className="list-disc pl-8 text-gray-700 space-y-2 mb-4">
                <li>Use the insurance money to rebuild, then sell the restored property</li>
                <li>Sell as-is and return unused insurance funds to the insurance company (most policies require this)</li>
              </ul>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Important:</strong> If you received an insurance payout designated for repairs but sell instead, you typically must return the unused portion. However, you can still sell to a cash buyer—we&apos;ll help you coordinate with your insurance company to ensure everything is handled properly.
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-black mb-4 flex items-center gap-3">
                <i className="ri-alert-line text-amber-600 text-2xl"></i>
                Scenario 2: Partial Insurance Coverage
              </h3>
              <p className="text-gray-700 mb-4">
                Your insurance payout doesn&apos;t cover the full restoration cost. This is extremely common due to:
              </p>
              <ul className="list-disc pl-8 text-gray-700 space-y-2 mb-4">
                <li>Depreciation clauses that reduce payouts for older homes</li>
                <li>Coverage limits that cap total payouts</li>
                <li>Exclusions for certain types of damage (smoke damage, water damage from firefighting)</li>
                <li>Underinsurance—the policy limit is less than actual replacement cost</li>
              </ul>
              <p className="text-gray-700 mb-4">
                In this scenario, you face a difficult choice: pay out-of-pocket to cover the gap, or sell as-is and move on.
              </p>
              <div className="bg-amber-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Cash sale advantage:</strong> You can sell immediately without spending additional money on repairs. The cash offer accounts for the insurance payout you received plus the property&apos;s remaining value.
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-black mb-4 flex items-center gap-3">
                <i className="ri-close-circle-line text-red-600 text-2xl"></i>
                Scenario 3: No Insurance or Denied Claim
              </h3>
              <p className="text-gray-700 mb-4">
                You have no insurance coverage, your policy lapsed, or your claim was denied. This is the most financially challenging situation.
              </p>
              <p className="text-gray-700 mb-4">
                Common reasons for denied fire damage claims:
              </p>
              <ul className="list-disc pl-8 text-gray-700 space-y-2 mb-4">
                <li>Policy lapsed due to non-payment</li>
                <li>Fire was caused by neglect or intentional act</li>
                <li>Property was vacant for extended period (many policies exclude vacant properties)</li>
                <li>Arson investigation is ongoing</li>
              </ul>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Your only realistic option:</strong> Sell as-is to a cash buyer. You can&apos;t afford to rebuild, and traditional buyers won&apos;t touch a fire damaged property. Cash buyers will still make offers based on land value and salvageable structure.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 text-white rounded-xl p-8 my-8">
            <h3 className="text-2xl font-bold mb-4">Working with Your Mortgage Lender</h3>
            <p className="text-gray-300 mb-4">
              If you have a mortgage, your lender likely has a clause requiring insurance proceeds to be used for repairs or applied to the loan balance. Here&apos;s how to handle this:
            </p>
            <ol className="list-decimal pl-8 text-gray-300 space-y-3">
              <li>
                <strong className="text-white">Contact your lender immediately</strong> after the fire. Explain the situation and your intention to sell.
              </li>
              <li>
                <strong className="text-white">Request a payoff statement</strong> that accounts for the insurance proceeds.
              </li>
              <li>
                <strong className="text-white">Coordinate the sale</strong> so insurance funds and sale proceeds are applied to pay off the mortgage at closing.
              </li>
              <li>
                <strong className="text-white">If you&apos;re underwater</strong> (owe more than the property is worth), discuss a short sale with your lender.
              </li>
            </ol>
            <div className="bg-white/10 p-4 rounded-lg mt-6">
              <p className="text-sm text-gray-200">
                <i className="ri-information-line text-[#D4AF37] mr-2"></i>
                We work with mortgage lenders daily and can help facilitate this process. We&apos;ve successfully closed hundreds of sales where insurance proceeds and sale funds were coordinated to satisfy the mortgage.
              </p>
            </div>
          </div>

          {/* Section 6 */}
          <h2 id="what-fire-damage-repairs-actually-cost-in-el-paso" className="text-4xl font-bold text-black mt-16 mb-6">
            What Fire Damage Repairs Actually Cost in El Paso
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Understanding repair costs helps you evaluate whether rebuilding makes financial sense or if selling as-is is the better option. Fire damage restoration costs vary dramatically based on the extent of damage.
          </p>

          <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden my-8">
            <div className="bg-gray-900 text-white px-6 py-4">
              <h3 className="text-xl font-bold">El Paso Fire Damage Restoration Cost Breakdown</h3>
            </div>
            <div className="divide-y divide-gray-200">
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-lg font-bold text-black">Minor Smoke Damage (One Room)</h4>
                    <p className="text-sm text-gray-600">Smoke odor, soot on walls, no structural damage</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#D4AF37]">$3,000 - $8,000</p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Professional cleaning and deodorization</li>
                  <li>• Repainting walls and ceiling</li>
                  <li>• HVAC duct cleaning</li>
                  <li>• Carpet replacement or deep cleaning</li>
                </ul>
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-lg font-bold text-black">Moderate Fire Damage (Multiple Rooms)</h4>
                    <p className="text-sm text-gray-600">Fire contained to one area, water damage from firefighting, some structural damage</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#D4AF37]">$15,000 - $50,000</p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Structural repairs (framing, drywall, flooring)</li>
                  <li>• Electrical system inspection and repairs</li>
                  <li>• Plumbing repairs if damaged</li>
                  <li>• Roof repairs if affected</li>
                  <li>• Complete interior restoration of affected areas</li>
                  <li>• Smoke and water damage remediation throughout home</li>
                </ul>
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-lg font-bold text-black">Severe Fire Damage (Whole House)</h4>
                    <p className="text-sm text-gray-600">Fire spread throughout home, major structural damage, roof compromised</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#D4AF37]">$75,000 - $200,000</p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Extensive structural repairs or partial rebuild</li>
                  <li>• Complete electrical system replacement</li>
                  <li>• Complete plumbing system inspection and repairs</li>
                  <li>• Roof replacement</li>
                  <li>• All interior finishes (flooring, drywall, paint, fixtures)</li>
                  <li>• HVAC system replacement</li>
                  <li>• Permits and inspections</li>
                </ul>
              </div>

              <div className="p-6 bg-red-50">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-lg font-bold text-black">Total Loss (Structure Unsalvageable)</h4>
                    <p className="text-sm text-gray-600">Complete structural failure, demolition required</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-red-600">$200,000 - $400,000+</p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Demolition and debris removal: $8,000 - $15,000</li>
                  <li>• Complete rebuild at current construction costs</li>
                  <li>• New foundation if damaged</li>
                  <li>• All new systems, finishes, and fixtures</li>
                  <li>• Permits, inspections, and architectural plans</li>
                </ul>
                <div className="mt-4 p-4 bg-white rounded-lg border border-red-200">
                  <p className="text-sm text-gray-700">
                    <strong>Reality check:</strong> For total loss properties, selling the land as-is is almost always more financially sensible than rebuilding, especially if insurance doesn&apos;t cover full replacement cost.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-[#D4AF37] p-6 my-8 rounded-r-lg">
            <p className="text-gray-800 font-semibold mb-2">
              <i className="ri-calculator-line text-[#D4AF37] mr-2"></i>
              Hidden Costs of Fire Damage Restoration
            </p>
            <p className="text-gray-700 mb-3">
              The estimates above are just the beginning. Factor in these additional costs:
            </p>
            <ul className="text-gray-700 space-y-2">
              <li>• <strong>Temporary housing:</strong> $1,500 - $3,000/month for 6-12 months = $9,000 - $36,000</li>
              <li>• <strong>Continued mortgage payments:</strong> During restoration period</li>
              <li>• <strong>Property taxes and insurance:</strong> Continue accruing</li>
              <li>• <strong>Storage for salvaged belongings:</strong> $100 - $300/month</li>
              <li>• <strong>Permit fees and inspections:</strong> $2,000 - $5,000</li>
              <li>• <strong>Cost overruns:</strong> Restoration projects typically exceed estimates by 15-30%</li>
            </ul>
          </div>

          {/* Section 7 */}
          <h2 id="the-cash-sale-process-for-fire-damaged-homes" className="text-4xl font-bold text-black mt-16 mb-6">
            The Cash Sale Process for Fire Damaged Homes
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Selling your fire damaged property to a cash buyer is straightforward and fast. Here&apos;s exactly how the process works from initial contact to closing:
          </p>

          <div className="space-y-6 my-8">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
              </div>
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-black mb-2">Initial Contact (Day 1)</h3>
                <p className="text-gray-700 mb-3">
                  Call us at <strong>915-745-7883</strong> or submit your property information through our website. We&apos;ll ask basic questions about the fire damage, your timeline, and your situation.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>What we need to know:</strong> Property address, date of fire, extent of damage, insurance status, mortgage balance, and your ideal timeline.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">2</span>
                </div>
              </div>
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-black mb-2">Property Evaluation (Day 2-3)</h3>
                <p className="text-gray-700 mb-3">
                  We schedule a walkthrough of the property. This typically takes 20-30 minutes. We assess the fire damage, structural integrity, and salvageable components.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>What we evaluate:</strong> Foundation and structural elements, roof condition, electrical and plumbing systems, extent of smoke and water damage, neighborhood comparables, and land value.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">3</span>
                </div>
              </div>
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-black mb-2">Cash Offer Presentation (Day 3-4)</h3>
                <p className="text-gray-700 mb-3">
                  We present a written cash offer with no obligations. The offer is based on the property&apos;s current condition, comparable sales, and estimated restoration costs.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Our offer includes:</strong> Purchase price, proposed closing date, proof of funds, and explanation of how we calculated the offer. Take your time to review—there&apos;s no pressure.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">4</span>
                </div>
              </div>
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-black mb-2">Acceptance and Contract (Day 5-7)</h3>
                <p className="text-gray-700 mb-3">
                  If you accept our offer, we prepare the purchase contract. You&apos;ll review it with your attorney if desired. We coordinate with your mortgage lender and insurance company if applicable.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>What happens now:</strong> We open escrow with a title company, order a title search, and begin coordinating all parties (lender, insurance, title company) for a smooth closing.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">5</span>
                </div>
              </div>
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-black mb-2">Title Work and Coordination (Day 8-12)</h3>
                <p className="text-gray-700 mb-3">
                  The title company conducts a title search to ensure clear ownership. We work with your mortgage lender to obtain a payoff statement and coordinate insurance proceeds if applicable.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Behind the scenes:</strong> We handle all communication with lenders, insurance companies, and the title company. You don&apos;t have to chase anyone down or coordinate multiple parties.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">6</span>
                </div>
              </div>
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-black mb-2">Closing (Day 7-14)</h3>
                <p className="text-gray-700 mb-3">
                  We close at a local title company. You sign the deed and transfer documents. We wire the funds, and you receive your proceeds immediately. The property is now ours, and you&apos;re free to move forward.
                </p>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-700">
                    <strong>What you walk away with:</strong> Cash in hand (or wired to your account), no more mortgage payments, no more property tax bills, no more insurance premiums, and no more stress about the fire damaged property.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-black text-white rounded-xl p-8 my-8">
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <i className="ri-time-line text-[#D4AF37] text-3xl"></i>
              Timeline Comparison: Traditional Sale vs. Cash Sale
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div className="bg-white/10 rounded-lg p-6">
                <h4 className="text-lg font-bold text-[#D4AF37] mb-4">Traditional Sale (After Repairs)</h4>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-3">
                    <i className="ri-time-line text-[#D4AF37] mt-1"></i>
                    <span><strong className="text-white">6-12 months:</strong> Restoration and repairs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-time-line text-[#D4AF37] mt-1"></i>
                    <span><strong className="text-white">2-4 weeks:</strong> Prepare and list property</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-time-line text-[#D4AF37] mt-1"></i>
                    <span><strong className="text-white">1-3 months:</strong> Marketing and showings</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-time-line text-[#D4AF37] mt-1"></i>
                    <span><strong className="text-white">30-45 days:</strong> Buyer financing and closing</span>
                  </li>
                </ul>
                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="text-2xl font-bold text-white">Total: 8-16 months</p>
                </div>
              </div>

              <div className="bg-[#D4AF37]/20 rounded-lg p-6 border-2 border-[#D4AF37]">
                <h4 className="text-lg font-bold text-[#D4AF37] mb-4">Cash Sale (As-Is)</h4>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-[#D4AF37] mt-1"></i>
                    <span><strong className="text-white">Day 1:</strong> Contact us</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-[#D4AF37] mt-1"></i>
                    <span><strong className="text-white">Day 2-3:</strong> Property walkthrough</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-[#D4AF37] mt-1"></i>
                    <span><strong className="text-white">Day 3-4:</strong> Receive cash offer</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-[#D4AF37] mt-1"></i>
                    <span><strong className="text-white">Day 7-14:</strong> Close and get paid</span>
                  </li>
                </ul>
                <div className="mt-6 pt-6 border-t border-[#D4AF37]">
                  <p className="text-2xl font-bold text-white">Total: 7-14 days</p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 8 */}
          <h2 id="fire-damage-disclosure-requirements-in-texas" className="text-4xl font-bold text-black mt-16 mb-6">
            Fire Damage Disclosure Requirements in Texas
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Texas law requires sellers to disclose material defects, and fire damage definitely qualifies. Here&apos;s what you must disclose and how to do it properly:
          </p>

          <div className="bg-white border-2 border-gray-200 rounded-xl p-8 my-8">
            <h3 className="text-2xl font-bold text-black mb-6">Required Disclosures for Fire Damaged Property</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-black mb-3 flex items-center gap-2">
                  <i className="ri-file-list-3-line text-[#D4AF37]"></i>
                  1. Seller&apos;s Disclosure Notice
                </h4>
                <p className="text-gray-700 mb-3">
                  Complete the Texas Real Estate Commission&apos;s Seller&apos;s Disclosure Notice (Form OP-H). In Section 7 (Condition of Property), you must check &quot;Yes&quot; for fire damage and provide details:
                </p>
                <ul className="list-disc pl-8 text-gray-700 space-y-2">
                  <li>Date of the fire</li>
                  <li>Cause of the fire (if known)</li>
                  <li>Extent of damage (which rooms/areas were affected)</li>
                  <li>What repairs, if any, have been completed</li>
                  <li>Whether insurance covered the damage</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-black mb-3 flex items-center gap-2">
                  <i className="ri-alert-line text-[#D4AF37]"></i>
                  2. Material Defect Disclosure
                </h4>
                <p className="text-gray-700 mb-3">
                  Beyond the standard form, you should provide a written statement detailing:
                </p>
                <ul className="list-disc pl-8 text-gray-700 space-y-2">
                  <li>Structural damage caused by the fire</li>
                  <li>Electrical system damage</li>
                  <li>Plumbing damage</li>
                  <li>Roof damage</li>
                  <li>Smoke and soot damage throughout the property</li>
                  <li>Water damage from firefighting efforts</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-black mb-3 flex items-center gap-2">
                  <i className="ri-shield-check-line text-[#D4AF37]"></i>
                  3. Insurance Claim Documentation
                </h4>
                <p className="text-gray-700 mb-3">
                  Provide copies of:
                </p>
                <ul className="list-disc pl-8 text-gray-700 space-y-2">
                  <li>Insurance claim documents</li>
                  <li>Adjuster&apos;s report</li>
                  <li>Payout statements</li>
                  <li>Any repair estimates or contractor bids</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-black mb-3 flex items-center gap-2">
                  <i className="ri-government-line text-[#D4AF37]"></i>
                  4. Fire Department and City Records
                </h4>
                <p className="text-gray-700">
                  If the fire department responded, there&apos;s a public record of the incident. Buyers can access this information, so it&apos;s better to disclose it upfront. Provide the fire department incident report if available.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border-l-4 border-red-600 p-6 my-8 rounded-r-lg">
            <p className="text-gray-800 font-semibold mb-2 flex items-center gap-2">
              <i className="ri-error-warning-line text-red-600"></i>
              Legal Consequences of Non-Disclosure
            </p>
            <p className="text-gray-700 mb-3">
              Failing to disclose fire damage can result in serious legal consequences:
            </p>
            <ul className="text-gray-700 space-y-2">
              <li>• <strong>Lawsuit for fraud:</strong> Buyer can sue for damages, including repair costs and legal fees</li>
              <li>• <strong>Contract rescission:</strong> Buyer can void the sale and demand their money back</li>
              <li>• <strong>Punitive damages:</strong> Courts may award additional damages for intentional concealment</li>
              <li>• <strong>Criminal charges:</strong> In extreme cases, non-disclosure can be prosecuted as fraud</li>
            </ul>
            <p className="text-gray-700 mt-4">
              <strong>Bottom line:</strong> Always disclose fire damage fully and honestly. Cash buyers expect fire damage—that&apos;s why they&apos;re buying the property. Full disclosure protects you legally and ensures a smooth transaction.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-6 my-8">
            <h4 className="text-lg font-semibold text-black mb-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-green-600"></i>
              How We Handle Disclosures
            </h4>
            <p className="text-gray-700 mb-3">
              When you sell to us, we make the disclosure process simple:
            </p>
            <ul className="text-gray-700 space-y-2">
              <li>• We provide the disclosure forms and help you complete them accurately</li>
              <li>• We review all documentation with you to ensure nothing is missed</li>
              <li>• We&apos;re buying the property as-is, so full disclosure doesn&apos;t reduce our offer</li>
              <li>• We handle all coordination with title companies and attorneys</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Our goal is to make the process as stress-free as possible while ensuring you&apos;re fully protected legally.
            </p>
          </div>

          {/* Section 9 */}
          <h2 id="how-we-evaluate-fire-damaged-properties-in-el-paso" className="text-4xl font-bold text-black mt-16 mb-6">
            How We Evaluate Fire Damaged Properties in El Paso
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Understanding how we calculate our cash offers helps you see that our pricing is fair and based on real market factors. Here&apos;s exactly how we evaluate fire damaged properties:
          </p>

          <div className="bg-white border-2 border-gray-200 rounded-xl p-8 my-8">
            <h3 className="text-2xl font-bold text-black mb-6">Our Evaluation Formula</h3>
            
            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-home-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-black">Step 1: Determine After Repair Value (ARV)</h4>
                    <p className="text-sm text-gray-600">What the property would be worth if fully restored</p>
                  </div>
                </div>
                <div className="ml-16">
                  <p className="text-gray-700 mb-3">
                    We analyze recent sales of comparable homes in your neighborhood that are in good condition. This gives us the ARV—what your property would sell for if it were fully repaired.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Example:</strong> Similar homes in your neighborhood recently sold for $220,000 - $240,000. We estimate your ARV at $230,000.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-black">Step 2: Calculate Repair Costs</h4>
                    <p className="text-sm text-gray-600">Realistic estimate of full restoration expenses</p>
                  </div>
                </div>
                <div className="ml-16">
                  <p className="text-gray-700 mb-3">
                    We estimate the cost to fully restore the property based on the extent of fire damage. This includes:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-3">
                    <li>Structural repairs</li>
                    <li>Electrical and plumbing work</li>
                    <li>Roof repairs or replacement</li>
                    <li>Interior finishes (drywall, flooring, paint, fixtures)</li>
                    <li>Smoke and water damage remediation</li>
                    <li>Permits and inspections</li>
                  </ul>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Example:</strong> Based on the fire damage, we estimate $65,000 in repair costs to fully restore the property.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-black">Step 3: Factor in Holding Costs</h4>
                    <p className="text-sm text-gray-600">Expenses during the restoration period</p>
                  </div>
                </div>
                <div className="ml-16">
                  <p className="text-gray-700 mb-3">
                    While we restore the property, we incur ongoing costs:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-3">
                    <li>Property taxes</li>
                    <li>Insurance</li>
                    <li>Utilities</li>
                    <li>Loan interest (if we finance the purchase)</li>
                  </ul>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Example:</strong> We estimate 6 months of holding costs at approximately $8,000.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-percent-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-black">Step 4: Account for Resale Costs</h4>
                    <p className="text-sm text-gray-600">Expenses when we sell the restored property</p>
                  </div>
                </div>
                <div className="ml-16">
                  <p className="text-gray-700 mb-3">
                    When we eventually sell the restored property, we&apos;ll pay:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1 mb-3">
                    <li>Real estate agent commissions (5-6%)</li>
                    <li>Closing costs</li>
                    <li>Seller concessions</li>
                  </ul>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Example:</strong> On a $230,000 sale, we estimate $15,000 in selling costs.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-black">Step 5: Include Profit Margin</h4>
                    <p className="text-sm text-gray-600">Our compensation for risk and effort</p>
                  </div>
                </div>
                <div className="ml-16">
                  <p className="text-gray-700 mb-3">
                    We&apos;re taking on significant risk by purchasing a fire damaged property. We need a profit margin to justify the investment, risk, and effort. Typical profit margins for fire damaged properties are 15-20% of ARV.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Example:</strong> On a $230,000 ARV, we target a $35,000 profit margin.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t-2 border-gray-200">
              <h4 className="text-xl font-bold text-black mb-4">Putting It All Together: Sample Calculation</h4>
              <div className="bg-gray-900 text-white rounded-xl p-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-3 border-b border-white/20">
                    <span>After Repair Value (ARV)</span>
                    <span className="font-bold text-[#D4AF37]">$230,000</span>
                  </div>
                  <div className="flex justify-between items-center text-red-300">
                    <span>− Repair Costs</span>
                    <span className="font-bold">−$65,000</span>
                  </div>
                  <div className="flex justify-between items-center text-red-300">
                    <span>− Holding Costs</span>
                    <span className="font-bold">−$8,000</span>
                  </div>
                  <div className="flex justify-between items-center text-red-300">
                    <span>− Resale Costs</span>
                    <span className="font-bold">−$15,000</span>
                  </div>
                  <div className="flex justify-between items-center text-red-300 pb-3 border-b border-white/20">
                    <span>− Profit Margin</span>
                    <span className="font-bold">−$35,000</span>
                  </div>
                  <div className="flex justify-between items-center text-2xl pt-3">
                    <span className="font-bold">Our Cash Offer</span>
                    <span className="font-bold text-[#D4AF37]">$107,000</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 mt-4 text-sm">
                This is a simplified example. Actual offers vary based on property condition, location, market conditions, and specific circumstances. We provide detailed breakdowns with every offer so you understand exactly how we arrived at the price.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-[#D4AF37] p-6 my-8 rounded-r-lg">
            <p className="text-gray-800 font-semibold mb-2">
              <i className="ri-information-line text-[#D4AF37] mr-2"></i>
              Why Our Offers Are Fair
            </p>
            <p className="text-gray-700 mb-3">
              Some homeowners initially think cash offers are &quot;too low.&quot; Here&apos;s the reality:
            </p>
            <ul className="text-gray-700 space-y-2">
              <li>• We&apos;re buying in as-is condition—you do zero work</li>
              <li>• We close in days, not months—you stop paying mortgage, taxes, and insurance immediately</li>
              <li>• We handle all repairs, permits, and contractor coordination</li>
              <li>• We take on all the risk—what if repair costs exceed estimates? What if the market drops?</li>
              <li>• You pay no agent commissions, no closing costs, no repair expenses</li>
            </ul>
            <p className="text-gray-700 mt-4">
              When you factor in the time, money, and stress you save, cash offers often net you more than attempting to restore and sell traditionally.
            </p>
          </div>

          {/* Section 10: FAQ */}
          <h2 id="frequently-asked-questions-about-selling-fire-damaged-property" className="text-4xl font-bold text-black mt-16 mb-6">
            Frequently Asked Questions About Selling Fire Damaged Property
          </h2>

          <div className="space-y-6 my-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-3 flex items-center gap-2">
                <i className="ri-question-line text-[#D4AF37]"></i>
                Can I sell my fire damaged house if I still have a mortgage?
              </h3>
              <p className="text-gray-700">
                Yes. The sale proceeds and any insurance payouts are used to pay off the mortgage at closing. If the combined amount exceeds your mortgage balance, you receive the difference. If you&apos;re underwater (owe more than the property is worth), we can help you negotiate a short sale with your lender.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-3 flex items-center gap-2">
                <i className="ri-question-line text-[#D4AF37]"></i>
                What if the fire was caused by arson or is under investigation?
              </h3>
              <p className="text-gray-700">
                You can still sell the property, but you must disclose the arson investigation. If you&apos;re under investigation, consult with an attorney before selling. If someone else committed arson (not you), this doesn&apos;t prevent the sale—just disclose it to the buyer.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-3 flex items-center gap-2">
                <i className="ri-question-line text-[#D4AF37]"></i>
                Do I have to clean up the fire damage before selling?
              </h3>
              <p className="text-gray-700">
                No. When you sell to a cash buyer, we purchase the property in its current condition. You don&apos;t clean, repair, or remove debris. We handle everything after closing.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-3 flex items-center gap-2">
                <i className="ri-question-line text-[#D4AF37]"></i>
                What if the city has condemned the property?
              </h3>
              <p className="text-gray-700">
                Condemned properties can still be sold. The buyer (us) takes on the responsibility of bringing the property up to code or demolishing it. Condemnation doesn&apos;t prevent the sale—it just affects the price since we&apos;ll need to address the code violations.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-3 flex items-center gap-2">
                <i className="ri-question-line text-[#D4AF37]"></i>
                How do taxes work when selling a fire damaged property?
              </h3>
              <p className="text-gray-700 mb-3">
                Tax implications depend on several factors:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Capital gains:</strong> If you sell for more than your cost basis, you may owe capital gains tax. However, if it was your primary residence for 2 of the last 5 years, you may qualify for the $250,000 (single) or $500,000 (married) exclusion.</li>
                <li><strong>Insurance proceeds:</strong> Insurance payouts for property damage are generally not taxable. However, if you receive more than your cost basis, the excess may be taxable.</li>
                <li><strong>Loss deduction:</strong> If you sell for less than your cost basis and it wasn&apos;t your primary residence, you may be able to deduct the loss.</li>
              </ul>
              <p className="text-gray-700 mt-3">
                Consult with a tax professional to understand your specific situation.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-3 flex items-center gap-2">
                <i className="ri-question-line text-[#D4AF37]"></i>
                Can I sell if I already received an insurance payout?
              </h3>
              <p className="text-gray-700">
                Yes. If you received insurance money designated for repairs but decide to sell instead, you typically need to return the unused portion to the insurance company. We can help coordinate this. The insurance payout is factored into our offer calculation.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-3 flex items-center gap-2">
                <i className="ri-question-line text-[#D4AF37]"></i>
                How quickly can I close on a fire damaged property?
              </h3>
              <p className="text-gray-700">
                We can close in as little as 7 days if you need to move quickly. Most fire damaged property sales close within 10-14 days. The timeline depends on title work, mortgage payoff coordination, and your preferred closing date.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-3 flex items-center gap-2">
                <i className="ri-question-line text-[#D4AF37]"></i>
                What if I can&apos;t find all my property documents after the fire?
              </h3>
              <p className="text-gray-700">
                Don&apos;t worry. We can help you obtain copies of necessary documents. The title company can pull property records, and we can request mortgage payoff statements from your lender. Lost documents don&apos;t prevent the sale.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-3 flex items-center gap-2">
                <i className="ri-question-line text-[#D4AF37]"></i>
                Do you buy fire damaged properties in all El Paso neighborhoods?
              </h3>
              <p className="text-gray-700">
                Yes. We buy fire damaged properties throughout El Paso and surrounding areas, including Socorro, Horizon City, Anthony, and Canutillo. Location doesn&apos;t disqualify your property—we evaluate every situation.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-3 flex items-center gap-2">
                <i className="ri-question-line text-[#D4AF37]"></i>
                What if my property is a total loss?
              </h3>
              <p className="text-gray-700">
                We still buy total loss properties. In these cases, we&apos;re primarily buying the land value. We&apos;ll demolish the structure and either rebuild or sell the cleared lot. Total loss properties often have the fastest closing timelines since there&apos;s no question about repair feasibility.
              </p>
            </div>
          </div>

          {/* Section 11: Final CTA */}
          <h2 id="take-the-first-step-toward-recovery" className="text-4xl font-bold text-black mt-16 mb-6">
            Take the First Step Toward Recovery
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            A house fire is one of the most traumatic experiences a homeowner can face. The property damage is devastating, but the emotional and financial toll can be even worse. You don&apos;t have to navigate this alone, and you don&apos;t have to spend months or years dealing with restoration, insurance companies, and uncertainty.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Selling your fire damaged property to a cash buyer gives you a clear path forward. You can close quickly, receive cash in hand, and move on with your life. No repairs. No cleanup. No agent commissions. No waiting.
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            We&apos;ve helped dozens of El Paso homeowners sell fire damaged properties and start fresh. We understand what you&apos;re going through, and we&apos;re here to make the process as simple and stress-free as possible.
          </p>

          <div className="bg-gradient-to-br from-[#D4AF37] to-[#B8941F] text-white rounded-3xl p-12 text-center my-12">
            <h3 className="text-3xl font-bold mb-4">
              Ready to Sell Your Fire Damaged Property?
            </h3>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Get a fair cash offer within 48 hours. No obligations, no pressure. Just honest answers and a straightforward path forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:9157457883"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-black text-lg font-semibold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-phone-fill mr-2"></i>Call 915-745-7883 Now
              </a>
              <Link
                to="/#contact-form"
                className="inline-flex items-center justify-center px-8 py-4 bg-black text-white text-lg font-semibold rounded-full hover:bg-gray-900 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
              >
                Get Your Cash Offer
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
            <p className="text-sm text-white/80 mt-6">
              Available 7 days a week • Free consultation • No obligations
            </p>
          </div>

          {/* Call to Action */}
          <div className="mt-16 p-12 bg-gradient-to-br from-black to-gray-900 rounded-3xl text-center">
            <h3 className="text-3xl font-bold text-white mb-4">
              Own a Fire Damaged Property? Get a Cash Offer Today.
            </h3>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              No repairs. No cleanup. No agent commissions. We buy fire damaged homes in any condition throughout El Paso. Close in as
              little as 7 days and move forward with your life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:9157457883"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#D4AF37] text-white text-lg font-semibold rounded-full hover:bg-[#B8941F] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill mr-2"></i>Call 915-745-7883
              </a>
              <Link
                to="/#contact-form"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-black text-lg font-semibold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Your Cash Offer
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>

          {/* Related Posts */}
          <div className="mt-24">
            <h3 className="text-3xl font-bold text-black mb-8">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Link
                to="/get-cash/sell-house-as-is-el-paso"
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer border border-gray-100"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=older%20residential%20home%20needing%20repairs%20and%20renovation%20in%20desert%20southwest%20setting%2C%20El%20Paso%20Texas%20style%20adobe%20house%20with%20weathered%20exterior%20showing%20age%20and%20wear%2C%20warm%20golden%20sunlight%20on%20distressed%20property%20with%20potential%2C%20professional%20real%20estate%20photography%20capturing%20fixer%20upper%20opportunity&width=600&height=400&seq=as-is-hero-001&orientation=landscape"
                    alt="Sell house as-is in El Paso"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">
                    AS-IS SALES
                  </span>
                  <h4 className="text-lg font-semibold text-black mb-2">How to Sell Your El Paso House As-Is</h4>
                  <p className="text-sm text-gray-600">No money for repairs? Sell as‑is and walk away with cash.</p>
                </div>
              </Link>

              <Link
                to="/get-cash/el-paso-homeowner-insurance-costs"
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer border border-gray-100"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=homeowner%20insurance%20policy%20documents%20and%20property%20tax%20notice%20stacked%20on%20kitchen%20counter%20next%20to%20house%20keys%20and%20calculator%2C%20warm%20natural%20lighting%20in%20modern%20southwestern%20home%20interior&width=600&height=400&seq=ins-related-fire-001&orientation=landscape"
                    alt="El Paso homeowner insurance costs"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">
                    INSURANCE
                  </span>
                  <h4 className="text-lg font-semibold text-black mb-2">El Paso Homeowner Insurance Costs</h4>
                  <p className="text-sm text-gray-600">How insurance and taxes combine to crush affordability.</p>
                </div>
              </Link>

              <Link
                to="/get-cash/avoiding-foreclosure-options"
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer border border-gray-100"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=foreclosure%20notice%20document%20with%20house%20keys%20and%20calculator%2C%20financial%20stress%20concept%2C%20professional%20documentation%20photography%20with%20neutral%20background%2C%20realistic%20business%20imagery&width=600&height=400&seq=blog-002&orientation=landscape"
                    alt="Avoiding foreclosure"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">
                    FORECLOSURE
                  </span>
                  <h4 className="text-lg font-semibold text-black mb-2">Avoiding Foreclosure: Your Options</h4>
                  <p className="text-sm text-gray-600">Alternatives to help you avoid losing your home.</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
